import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
} from '@mui/material';
import clsx from 'clsx';
import type { ClientType, UserType } from 'entities/user';
import { MuiTelInputInfo } from 'mui-tel-input';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from 'shared/api';
import { ValidationRegular, validateINN } from 'shared/model/constants/validations';
import { ButtonCustom } from 'shared/ui/button-custom';
import { CheckboxInput } from 'shared/ui/checkbox-input';
import { PhoneInput } from 'shared/ui/phone-input';
import { TextInput } from 'shared/ui/text-input';
import styles from './agent-register.module.scss';
import { translateError } from './errorTranslations';

export const AgentRegister = () => {
  const [goodReg, setGoodReg] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rules = (event: any) => {
    event?.preventDefault();
    const link = document.createElement('a');
    link.href =
      'https://dashboard.tlspoisk.ru/%D0%9F%D0%BE%D0%BB%D0%B8%D1%82%D0%B8%D0%BA%D0%B0_%D0%B2_%D0%BE%D1%82%D0%BD%D0%BE%D1%88%D0%B5%D0%BD%D0%B8%D0%B8_%D0%BE%D0%B1%D1%80%D0%B0%D0%B1%D0%BE%D1%82%D0%BA%D0%B8_%D0%BF%D0%B5%D1%80%D1%81%D0%BE%D0%BD%D0%B0%D0%BB%D1%8C%D0%BD%D1%8B%D1%85_%D0%B4%D0%B0%D0%BD%D0%BD%D1%8B%D1%85.pdf';
    link.download = 'Политика в отношении обработки персональных данных.pdf';
    link.click();
  };

  const [userType, setUserType] = useState<UserType>('agent');
  const [clientType, setClientType] = useState<ClientType>('entrepreneur');

  const [check, setCheck] = useState(false);
  const [check_err, setCheck_Err] = useState(false);
  const [inn, setInn] = useState('');
  const [bio, setBio] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [pass_repeat, setPass_repeat] = useState('');

  const [bioErr, setBioErr] = useState(false);
  const [innErr, setInnErr] = useState(false);
  const [phoneErr, setPhoneErr] = useState(false);
  const [emailErr, setEmailErr] = useState(false);
  const [passErr, setPassErr] = useState(false);
  const [pass_repeatErr, setPass_repeatErr] = useState(false);

  // Добавляем состояния для сообщений об ошибках
  const [bioErrMsg, setBioErrMsg] = useState('');
  const [innErrMsg, setInnErrMsg] = useState('');
  const [phoneErrMsg, setPhoneErrMsg] = useState('');
  const [emailErrMsg, setEmailErrMsg] = useState('');
  const [passErrMsg, setPassErrMsg] = useState('');
  const [passRepeatErrMsg, setPassRepeatErrMsg] = useState('');

  const [loading, setLoading] = useState(false);

  const handleChangeInn = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInn(value || '');

    // Проверка формата и алгоритма контрольной суммы ИНН
    const validInn = validateINN(value);

    setInnErr(!validInn);
    setInnErrMsg('');
  };
  const handleChangeBio = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBio(value);
    const bioValid = !(value?.toString().length && ValidationRegular.BIO.test(value?.toString()));

    setBioErr(bioValid);
    setBioErrMsg('');
  };
  const handleChangePhone = (value: string, _info: MuiTelInputInfo) => {
    let formatted = value.replace(/[^\d+]/g, '');
    if (formatted.startsWith('8')) {
      formatted = '+7' + formatted.slice(1);
    }
    if (!formatted.startsWith('+7')) {
      formatted = '+7';
    }
    const digitsOnly = formatted.replace(/\D/g, '');
    if (digitsOnly.length > 11) {
      formatted = '+' + digitsOnly.slice(0, 11);
    }
    setPhone(formatted);
    const isValid = /^\+7\d{10}$/.test(formatted);
    setPhoneErr(!isValid);
    setPhoneErrMsg('');
  };
  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    const emailValid = !(
      value?.toString().length && ValidationRegular.MAIL.test(value?.toString())
    );

    setEmailErr(emailValid);
    setEmailErrMsg('');
  };
  const handleChangePass = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPass(event.target.value);
    setPassErr(false);
    setPassErrMsg('');
  };
  const handleChangePass_repeat = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPass_repeat(event.target.value);
    setPass_repeatErr(false);
    setPassRepeatErrMsg('');
  };
  const handlePastePass_repeat = (e: any) => {
    e.preventDefault();
  };
  const handleChangeCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheck(e.target.checked);
    e.target.checked ? setCheck_Err(false) : setCheck_Err(true);
  };

  const handleSubmit = async () => {
    setCheck_Err(!check);

    // Сброс сообщений об ошибках
    setBioErrMsg('');
    setInnErrMsg('');
    setPhoneErrMsg('');
    setEmailErrMsg('');
    setPassErrMsg('');
    setPassRepeatErrMsg('');

    const formData = new FormData();

    if (bio?.toString().length && ValidationRegular.BIO.test(bio?.toString())) {
      formData.append('bio', bio);
      setBioErr(false);
    } else {
      setBioErr(true);
    }

    if (phone?.toString().length) {
      formData.append('phone', phone);
      setPhoneErr(false);
    } else {
      setPhoneErr(true);
    }

    if (email?.toString()?.trim().length && ValidationRegular.MAIL.test(email?.toString())) {
      formData.append('email', email.toString().trim().toLowerCase());
      setEmailErr(false);
    } else {
      setEmailErr(true);
    }

    if (pass?.toString()?.trim().length && pass_repeat.toString().length && pass == pass_repeat) {
      formData.append('password', pass.toString().trim());
      setPassErr(false);
      setPass_repeatErr(false);
    } else {
      setPassErr(true);
      setPass_repeatErr(true);
    }

    if (validateINN(inn)) {
      formData.append('inn', inn);
      setInnErr(false);
    } else {
      setInnErr(true);
    }

    formData.append('user_type', userType);
    if (userType === 'client') {
      formData.append('user_subtype', clientType);
    }

    const referalId = searchParams.get('referal');
    if (referalId) {
      formData.append('referal_id', referalId);
    }

    if (
      !bioErr &&
      !innErr &&
      !phoneErr &&
      !emailErr &&
      check &&
      !passErr &&
      !pass_repeatErr &&
      pass == pass_repeat
    ) {
      try {
        setLoading(true);
        const response = await apiClient.post('/new-agent', formData);
        setLoading(false);
        setGoodReg(true);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        window.ym && window.ym?.(105273634, 'reachGoal', 'submit_register_form_success');
      } catch (error: any) {
        // Обработка ошибок валидации
        if (error.response.data.error === 'User already exists') {
          setEmailErr(true);
          setEmailErrMsg('Пользователь с таким E-mail уже существует');
          setInnErr(true);
          setInnErrMsg('Пользователь с таким ИНН уже существует');
        } else if (error.response?.data?.error?.details?.errors) {
          const errors = error.response.data.error.details.errors;
          errors.forEach((err: any) => {
            const field = err.path[0];
            const message = translateError(err.message);

            if (field === 'bio') {
              setBioErr(true);
              setBioErrMsg(message);
            }
            if (field === 'inn') {
              setInnErr(true);
              setInnErrMsg(message);
            }
            if (field === 'phone') {
              setPhoneErr(true);
              setPhoneErrMsg(message);
            }
            if (field === 'email') {
              setEmailErr(true);
              setEmailErrMsg(message);
            }
            if (field === 'password') {
              setPassErr(true);
              setPassErrMsg(message);
            }
          });
        }
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const renderBlock = () => {
    if (goodReg) {
      return (
        <Box className={`${styles.goodRegInfo}, ${styles.loginPage}`}>
          <Box className={styles.loginTitleBlock}>
            <h2 className={styles.loginTitle}>
              <span className={styles.loginTitleBlack}>Благодарим за регистрацию!</span>
            </h2>
            <span className={styles.loginDesc}>
              Наши менеджеры в ближайшее время подтвердят вашу учетную запись в личном кабинете TLS
              Group. Уведомление об успешной верификации придет на e-mail, указанный при
              регистрации.
            </span>
          </Box>
          <Box className={styles.btnBlock}>
            <Box className={styles.submitBtn}>
              <ButtonCustom onClick={() => navigate('/auth/')}>
                Перейти на окно входа в личный кабинет
              </ButtonCustom>
            </Box>
            <span className={styles.spanBtnBlock}>или</span>
            <Box className={`${styles.submitBtn} ${styles.blackBtn}`}>
              <ButtonCustom onClick={() => navigate('/')}>Вернуться на сайт</ButtonCustom>
            </Box>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box className={styles.loginPage}>
          <Box className={styles.loginTitleBlock}>
            <h2 className={styles.loginTitle}>
              <span className={styles.loginTitleBlack}>
                Введите ваши данные для регистрации в личном кабинете
              </span>
            </h2>
          </Box>
          <Box className={styles.loginForm}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={userType}
                onChange={(event, newValue) => setUserType(newValue)}
                centered
                orientation='vertical'
                className={styles.tabsMobile}
              >
                <Tab label='Клиент' value='client' />
                <Tab label='Агент' value='agent' />
                <Tab label='Лизинговая компания' value='leasing' />
                <Tab label='Поставщик' value='supplier' />
              </Tabs>
              <Tabs
                value={userType}
                onChange={(event, newValue) => setUserType(newValue)}
                centered
                className={styles.tabsDesktop}
              >
                <Tab label='Клиент' value='client' />
                <Tab label='Агент' value='agent' />
                <Tab label='Лизинговая компания' value='leasing' />
                <Tab label='Поставщик' value='supplier' />
              </Tabs>
            </Box>
            <Box className={styles.agentRegForm}>
              {['agent', 'client'].includes(userType) && (
                <FormControl>
                  <FormLabel id='demo-radio-buttons-group-label'>Тип клиента</FormLabel>
                  <RadioGroup
                    defaultValue='legal'
                    name='client-type'
                    onChange={(event) => setClientType(event.target.value as ClientType)}
                  >
                    <FormControlLabel value='legal' control={<Radio />} label='Юридическое лицо' />
                    <FormControlLabel
                      value='entrepreneur'
                      control={<Radio />}
                      label='Индивидуальный предприниматель'
                    />
                    <FormControlLabel
                      value='individual'
                      control={<Radio />}
                      label='Физическое лицо'
                    />
                    <FormControlLabel
                      value='self-employed'
                      control={<Radio />}
                      label='Самозанятый'
                    />
                  </RadioGroup>
                </FormControl>
              )}
              <Box className={styles.inputWrapper}>
                <span className={styles.inputLabel}>ФИО</span>
                <TextInput
                  sx={{ '.MuiOutlinedInput-notchedOutline': { border: '1px solid #B5D4F9' } }}
                  name={'bio'}
                  value={bio}
                  onChange={handleChangeBio}
                  error={bioErr}
                  helperText={bioErrMsg}
                  type={'text'}
                  placeholder={'ФИО'}
                />
              </Box>
              <Box className={styles.inputWrapper}>
                <span className={styles.inputLabel}>ИНН</span>
                <TextInput
                  sx={{
                    '.MuiOutlinedInput-notchedOutline': { border: '1px solid #B5D4F9' },
                    '.Mui-error': { fontSize: '12px' },
                  }}
                  name={'inn'}
                  value={inn}
                  error={innErr}
                  helperText={innErrMsg}
                  onChange={handleChangeInn}
                  type={'text'}
                  placeholder={'ИНН'}
                  required
                />
              </Box>
              <Box className={styles.inputWrapper}>
                <span className={styles.inputLabel}>E-mail</span>
                <TextInput
                  sx={{ '.MuiOutlinedInput-notchedOutline': { border: '1px solid #B5D4F9' } }}
                  name={'email'}
                  value={email || ''}
                  onChange={handleChangeEmail}
                  error={emailErr}
                  helperText={emailErrMsg}
                  type={'email'}
                  required
                  placeholder={'E-mail'}
                />
              </Box>
              <Box className={styles.inputWrapper}>
                <span className={styles.inputLabel}>Телефон</span>
                <PhoneInput
                  onChange={handleChangePhone}
                  value={phone}
                  error={phoneErr}
                  helperText={phoneErrMsg}
                  name={'phone'}
                  required
                  placeholder={'Введите телефон'}
                  sx={{ '.MuiOutlinedInput-notchedOutline': { border: '1px solid #B5D4F9' } }}
                />
              </Box>
              <Box className={styles.inputWrapper}>
                <span className={styles.inputLabel}>Пароль</span>
                <TextInput
                  sx={{ '.MuiOutlinedInput-notchedOutline': { border: '1px solid #B5D4F9' } }}
                  name={'pass'}
                  value={pass}
                  onChange={handleChangePass}
                  error={passErr || pass !== pass_repeat}
                  helperText={passErrMsg}
                  type={'password'}
                  placeholder={'Пароль'}
                />
              </Box>
              <Box className={styles.inputWrapper}>
                <span className={styles.inputLabel}>Повторите пароль</span>
                <TextInput
                  sx={{ '.MuiOutlinedInput-notchedOutline': { border: '1px solid #B5D4F9' } }}
                  onPaste={handlePastePass_repeat}
                  name={'pass_repeat'}
                  value={pass_repeat}
                  onChange={handleChangePass_repeat}
                  error={pass_repeatErr || pass !== pass_repeat}
                  helperText={passRepeatErrMsg}
                  type={'password'}
                  placeholder={'Повторите пароль'}
                />
              </Box>
            </Box>
            <Box className={styles.accept}>
              <FormControlLabel
                sx={{
                  margin: 0,
                  '.MuiFormControlLabel-label': {
                    fontSize: '16px',
                    fontWeight: 500,
                    letterSpacing: '0.02em',
                    color: '#96A8B8',
                  },
                }}
                control={
                  <CheckboxInput
                    className={clsx([check_err && `${styles.accept_err}`])}
                    name='remember'
                    checked={check}
                    onChange={handleChangeCheck}
                  />
                }
                label={
                  <span className={styles.grayRules}>
                    Я согласен с{' '}
                    <div className={styles.blueRules} onClick={() => rules(event)}>
                      политикой обработки персональных данных
                    </div>
                  </span>
                }
              />
            </Box>
            <Box className={styles.submitBtn}>
              <ButtonCustom disabled={loading} onClick={handleSubmit}>
                {loading ? 'Подождите...' : 'Создать аккаунт'}
              </ButtonCustom>
            </Box>
          </Box>
        </Box>
      );
    }
  };

  return <>{renderBlock()}</>;
};
